/**
 * Slice 45 — Supply forecast hook (leave-out rules + upcoming stays + past-counts fallback).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useInventoryItems } from "@/hooks/useInventory";
import { useInventoryPredictions, type InventoryPrediction } from "@/hooks/useInventoryPredictions";
import { parseGuestAmenityStagingFromChecklist } from "@/lib/guestAmenityStaging";
import { getInStockCount } from "@/lib/inventoryQuantity";
import {
  FORECAST_ATTENTION,
  collectLeaveOutRulesByItemId,
  forecastFromPastCounts,
  forecastLeaveOutItem,
  statusSortKey,
  type SupplyForecastRow,
  type UpcomingStay,
} from "@/lib/supplyForecast";

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysStr(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Lean checklist fetch for leave-out rules — no placeholder, so we know when rules are real. */
function useLeaveOutStagingChecklists(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["checklists-leave-out-staging", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("checklists")
        .select("id, is_default, type, guest_amenity_staging")
        .eq("property_id", propertyId)
        .order("is_default", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!propertyId,
    staleTime: 1000 * 60,
  });
}

function useUpcomingStaysForForecast(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["supply-forecast-stays", propertyId],
    queryFn: async (): Promise<UpcomingStay[]> => {
      if (!propertyId) return [];
      const from = todayStr();
      const to = plusDaysStr(30);

      const { data: tasks, error } = await supabase
        .from("cleaning_tasks")
        .select("id, scheduled_date, guest_count, reservation_id, status")
        .eq("property_id", propertyId)
        .in("status", ["scheduled", "in_progress"])
        .gte("scheduled_date", from)
        .lte("scheduled_date", to)
        .order("scheduled_date", { ascending: true });

      if (error) throw error;
      const rows = tasks ?? [];
      const reservationIds = [
        ...new Set(
          rows
            .map((t) => t.reservation_id)
            .filter((id): id is string => typeof id === "string" && id.length > 0),
        ),
      ];

      let resById = new Map<
        string,
        { guest_count: number | null; guest_name: string | null; start_date: string | null }
      >();
      if (reservationIds.length > 0) {
        const { data: res } = await supabase
          .from("reservations")
          .select("id, guest_count, guest_name, start_date")
          .in("id", reservationIds);
        resById = new Map(
          (res ?? []).map((r) => [
            r.id,
            {
              guest_count: typeof r.guest_count === "number" ? r.guest_count : null,
              guest_name: r.guest_name ?? null,
              start_date: r.start_date ?? null,
            },
          ]),
        );
      }

      return rows.map((t) => {
        const res = t.reservation_id ? resById.get(t.reservation_id) : undefined;
        const guest_count =
          typeof res?.guest_count === "number"
            ? res.guest_count
            : typeof t.guest_count === "number"
              ? t.guest_count
              : null;
        return {
          id: t.id,
          date: t.scheduled_date,
          guest_count,
          guest_name: res?.guest_name ?? null,
        };
      });
    },
    enabled: !!propertyId,
    staleTime: 1000 * 60,
  });
}

export type SupplyForecastResult = {
  rows: SupplyForecastRow[];
  stays: UpcomingStay[];
  hasAnyLeaveOutRule: boolean;
  prefersLeaveOut: boolean;
  attention: SupplyForecastRow[];
  /** Default (or first) checklist id — for “Set leave-out rules” deep link. */
  defaultChecklistId: string | null;
  /** True only while inventory + staging checklists are loading (not past RPC). */
  isLoading: boolean;
};

export function useSupplyForecast(propertyId: string | undefined): SupplyForecastResult {
  const { data: items = [], isLoading: itemsLoading } = useInventoryItems(propertyId);
  const { data: stagingChecklists = [], isLoading: stagingLoading } =
    useLeaveOutStagingChecklists(propertyId);
  const { data: stays = [] } = useUpcomingStaysForForecast(propertyId);
  const { data: pastPredictions = [] } = useInventoryPredictions(propertyId);

  const ruleByItem = useMemo(
    () => collectLeaveOutRulesByItemId(stagingChecklists, parseGuestAmenityStagingFromChecklist),
    [stagingChecklists],
  );

  const defaultChecklistId = useMemo(() => {
    if (!stagingChecklists.length) return null;
    const cleaning = stagingChecklists.filter(
      (c) => (c as { type?: string | null }).type === "cleaning" || !(c as { type?: string | null }).type,
    );
    const pool = cleaning.length ? cleaning : stagingChecklists;
    const def = pool.find((c) => c.is_default);
    return (def ?? pool[0])?.id ?? null;
  }, [stagingChecklists]);

  const pastById = useMemo(() => {
    const m = new Map<string, InventoryPrediction>();
    for (const p of pastPredictions) m.set(p.item_id, p);
    return m;
  }, [pastPredictions]);

  const rows = useMemo((): SupplyForecastRow[] => {
    const unitItems = (items as Array<Record<string, unknown>>).filter((i) => {
      const scope = i.inventory_scope;
      if (scope === "unit") return false;
      const qt = String(i.quantity_type || "units").toLowerCase();
      return qt !== "fullness";
    });

    const out: SupplyForecastRow[] = [];
    for (const raw of unitItems) {
      const item_id = String(raw.id);
      const item_name = String(raw.item_name || "Item");
      const current_stock = getInStockCount(
        raw as { in_stock_qty?: number | null; current_count?: number | null },
      );
      const threshold_count =
        typeof raw.threshold_count === "number" && Number.isFinite(raw.threshold_count)
          ? Math.floor(raw.threshold_count)
          : 1;

      const linked = ruleByItem.get(item_id);
      if (linked) {
        out.push(
          forecastLeaveOutItem({
            item_id,
            item_name,
            current_stock,
            threshold_count,
            rule: linked.rule,
            plan: linked.plan,
            stays,
          }),
        );
        continue;
      }

      const past = pastById.get(item_id);
      if (past) {
        out.push(
          forecastFromPastCounts({
            item_id,
            item_name,
            current_stock: past.current_stock,
            threshold_count: past.threshold_count,
            avg_per_turnover: Number(past.avg_per_turnover) || 0,
            turnovers_until_empty: past.turnovers_until_empty,
            predicted_runout_date: past.predicted_runout_date,
            submission_count: past.submission_count,
            restock_qty: past.restock_qty,
            status: past.status,
            upcoming_turnovers_30d: past.upcoming_turnovers_30d,
          }),
        );
      } else {
        out.push({
          item_id,
          item_name,
          current_stock,
          threshold_count,
          source: "past_counts",
          out_next_30d: null,
          after: null,
          stay_count: stays.length,
          order_by_label: "Need more data",
          status: "insufficient_data",
          pulls: [],
          submission_count: 0,
        });
      }
    }

    out.sort((a, b) => {
      const s = statusSortKey(a.status) - statusSortKey(b.status);
      if (s !== 0) return s;
      return a.item_name.localeCompare(b.item_name);
    });
    return out;
  }, [items, ruleByItem, stays, pastById]);

  const hasAnyLeaveOutRule = ruleByItem.size > 0;
  const prefersLeaveOut = rows.some((r) => r.source === "leave_out");
  const attention = rows.filter((r) => FORECAST_ATTENTION.includes(r.status));

  return {
    rows,
    stays,
    hasAnyLeaveOutRule,
    prefersLeaveOut,
    attention,
    defaultChecklistId,
    // Do not wait on past-predictions RPC (cold starts ~10s) or stays.
    isLoading: itemsLoading || stagingLoading,
  };
}
